import { endOfDay, format, getHours, getMinutes, isMatch, isValid, parse, set, startOfDay } from 'date-fns';
import { DATE_FORMAT } from '../constants';
import { TimeOption } from '../../time-picker/TimePickerOptions';

/**
 * @param value string
 * @param dateFormat string [default='yyyy.MM.dd']
 * @param strongCheck boolean [default=false] 값이 없는 경우, 날짜 입력중인 경우 (dateFormat.length 와 비교) error 추가 판단
 */
export function checkInputValidation(value: string, dateFormat = DATE_FORMAT, strongCheck = false) {
  if (strongCheck && !value) {
    return false;
  }

  if (!/^[0-9|.|\s|:]+$/.test(value)) {
    return false;
  }

  if (!strongCheck && value.length < dateFormat.length) {
    return true;
  }

  if (!isMatch(value, dateFormat)) {
    return false;
  }

  return value.length === dateFormat.length;
}

export function isDisabledTime(
  value: string,
  timeHourPickerOptions: TimeOption[],
  timeMinutePickerOptions: TimeOption[],
) {
  if (!value) {
    return false;
  }

  const currentDate = convertStringToDate(value);

  return Boolean(
    !timeMinutePickerOptions.find((option) => option.value === Number(getMinutes(currentDate))) ||
      timeHourPickerOptions.find((option) => option.value === Number(getHours(currentDate)))?.disabled ||
      timeMinutePickerOptions.find((option) => option.value === Number(getMinutes(currentDate)))?.disabled,
  );
}

export function isExactDateMatch(input: string, formatString: string) {
  const parsedDate = parse(input, formatString, new Date());
  const isValidDate = isValid(parsedDate);
  if (isValidDate) {
    const formattedDate = format(parsedDate, formatString);
    return input === formattedDate;
  } else {
    return false;
  }
}

export function convertStringToDate(value: string): Date {
  const [date, time] = value.split(' ');
  const newFormat = date.replaceAll('.', '-');

  if (time) {
    return new Date(`${newFormat}T${time}`);
  }
  return new Date(newFormat);
}

export const convertDateToDateWithTime = (
  date: Date,
  time?: Date | null,
  value?: {
    hours?: number;
    minutes?: number;
    seconds?: number;
    milliseconds?: number;
  },
) => {
  const defaultTime = value ?? { hours: 0, minutes: 0, seconds: 0 };
  const { hours = 0, minutes = 0, seconds = 0, milliseconds = 0 } = defaultTime;
  const hasTime = !!time && time !== null;

  return set(date, {
    hours: hasTime ? getHours(time) : hours,
    minutes: hasTime ? getMinutes(time) : minutes,
    seconds,
    milliseconds,
  });
};

export const getTime = (times: TimeOption[], time?: number) => {
  const basic = times.find(({ disabled }) => !disabled)?.value;
  if (!time) {
    return basic;
  }
  const option = times.find((option) => option.value === time);
  if (!option || option.disabled) {
    return basic;
  }
  return option.value;
};

export const createOnSelectParams = (
  from: Date | undefined,
  to: Date | undefined,
  showTimePicker: boolean,
  selectedTimeRange?: { from?: Date | null; to?: Date | null },
) => {
  if (showTimePicker) {
    return {
      selected_from: from
        ? selectedTimeRange?.from
          ? convertDateToDateWithTime(from, selectedTimeRange?.from)
          : startOfDay(from)
        : undefined,
      selected_to: to
        ? selectedTimeRange?.to
          ? convertDateToDateWithTime(to, selectedTimeRange.to, { seconds: 59, milliseconds: 999 })
          : endOfDay(to)
        : undefined,
    };
  }

  return {
    selected_from: from ? startOfDay(from) : undefined,
    selected_to: to ? endOfDay(to) : undefined,
  };
};
